import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Spinner from './Spinner';
import { Icon, AlertTriangle, type LucideIcon } from './Icon';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** `neutral` for a confirmation that changes things without destroying them. */
  tone?: 'danger' | 'neutral';
  icon?: LucideIcon;
}

const TONE = {
  danger: {
    badge: 'bg-red-50 dark:bg-red-900/20',
    icon: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-600',
  },
  neutral: {
    badge: 'bg-teal-50 dark:bg-teal-900/20',
    icon: 'text-teal-600 dark:text-teal-400',
    button: 'bg-teal-600 hover:bg-teal-700',
  },
} as const;

export default function ConfirmModal({
  open, title, body, onConfirm, onCancel,
  confirmLabel, cancelLabel, loading = false, tone = 'danger', icon = AlertTriangle,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const colors = TONE[tone];
  return (
    <Modal open={open} onClose={onCancel} title="" size="sm">
      <div className="-mt-8 flex flex-col items-center text-center">
        <div className={`w-11 h-11 rounded-full ${colors.badge} flex items-center justify-center mb-3`}>
          <Icon as={icon} size={20} className={colors.icon} />
        </div>
        <h2 className="font-sans font-semibold text-base text-slate-900 dark:text-slate-100 mb-1">{title}</h2>
        {body && <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">{body}</p>}
        {!body && <div className="mb-4" />}
        <div className="flex gap-2 w-full">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 btn-secondary text-sm"
          >
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 ${colors.button} text-white text-sm font-semibold py-2 rounded-xl transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2`}
          >
            {loading ? <Spinner size="sm" /> : (confirmLabel ?? t('common.yesDelete'))}
          </button>
        </div>
      </div>
    </Modal>
  );
}
